/**
 * Hotel Validate Coupon API
 *
 * Handles hotel coupon validation/apply/remove API calls via HOTEL_REVIEW_MCP API.
 * Uses MCP format with action_type="Validate-Coupon".
 */
import type {
  HotelReviewPrice,
  FareBreakupData,
  HotelValidateCouponMCPRequest,
  MCPDeviceInfo,
  MCPLocationContext,
} from '@contracts';
import { HOTELS_ENDPOINTS } from '@/api/endpoints';
import { logger } from '@/shared/utils/logger';
import { baseFetch, createApiError, getCBContext } from './baseApi';
import { generateUUID } from '@/shared/utils/uuid';

// ============================================
// Error Class
// ============================================

export const HotelValidateCouponError = createApiError('HotelValidateCouponError');

// ============================================
// Request Types
// ============================================

export interface HotelValidateCouponRequestParams {
  sessionId: string;
  userId: string;
  /** Transaction key from booking review */
  txnKey: string;
  /** Coupon code to validate/apply */
  couponCode: string;
  /** Whether to remove the coupon */
  removeCoupon?: boolean | undefined;
  /** Hotel name */
  hotelName: string;
  /** Hotel ID */
  hotelId: string;
  /** City name */
  city?: string | undefined;
  /** City code (e.g., CTGOI for Goa) */
  cityCode?: string | undefined;
}

// ============================================
// Response Types
// ============================================

export interface PriceSummaryData {
  reviewPrice: HotelReviewPrice | null;
  fareBreakup: FareBreakupData | null;
}

export interface HotelValidateCouponResponse {
  type: 'hotels-validate-coupon';
  success: boolean;
  priceSummary: PriceSummaryData | null;
  statusMessage: string | null;
  ratePlanList: unknown[];
  /** The coupon code that is now applied (null if removed or none) */
  appliedCouponCode: string | null;
  error?: { code: string; message: string };
}

// ============================================
// Raw API Response Type
// ============================================

interface RawResponse {
  success?: boolean;
  priceSummary?: { reviewPrice?: HotelReviewPrice; fareBreakup?: FareBreakupData } | null;
  statusMessage?: string | null;
  ratePlanList?: unknown[];
  /** The coupon code that is now applied (null if removed or none) */
  appliedCouponCode?: string | null;
  error?: { code: string; message: string };
}

// ============================================
// Helper Functions
// ============================================

function transformPriceSummary(raw: RawResponse['priceSummary']): PriceSummaryData | null {
  if (!raw) return null;
  return {
    reviewPrice: raw.reviewPrice ?? null,
    fareBreakup: raw.fareBreakup ?? null,
  };
}

/**
 * Build device info for MCP request
 */
function buildDeviceInfo(): MCPDeviceInfo {
  const { deviceId } = getCBContext();
  return {
    device_id: deviceId,
    app_ver: '1.21.1',
    device_type: 'DESKTOP',
    platform: 'DESKTOP',
  };
}

/**
 * Build location context for MCP validate coupon request
 */
function buildLocationContext(params: HotelValidateCouponRequestParams): MCPLocationContext {
  return {
    hotel: params.hotelName,
    hotel_id: params.hotelId,
    city: params.city,
    city_code: params.cityCode,
  };
}

/**
 * Build the MCP payload for validate coupon request
 */
export function buildMCPPayload(
  params: HotelValidateCouponRequestParams,
): HotelValidateCouponMCPRequest {
  const { authToken } = getCBContext();

  return {
    request_id: generateUUID(),
    conversation_id: params.sessionId,
    uuid: params.userId,
    auth: authToken,
    device_info: buildDeviceInfo(),
    rephrased_query: params.removeCoupon
      ? `Remove coupon code ${params.couponCode}`
      : `Apply coupon code ${params.couponCode}`,
    intents: ['Review'],
    action_type: 'Validate-Coupon',
    location_context: buildLocationContext(params),
    txnKey: params.txnKey,
    couponCode: params.couponCode,
    removeCoupon: params.removeCoupon ?? false,
    logged_in: authToken !== '',
    funnelSource: 'HOTELS',
  };
}

// ============================================
// API Function
// ============================================

/**
 * Validate/apply/remove coupon from the API
 * POST /hotels/validate-coupon?sessionId=...&userId=...
 *
 * Sends MCP payload with structure:
 * {
 *   request_id, conversation_id, uuid, auth, device_info,
 *   rephrased_query, intents, action_type: "Validate-Coupon",
 *   location_context: { hotel, hotel_id, city },
 *   txnKey, couponCode, logged_in, funnelSource
 * }
 */
export async function fetchHotelValidateCoupon(
  params: HotelValidateCouponRequestParams,
): Promise<HotelValidateCouponResponse> {
  const payload = buildMCPPayload(params);

  const { data, error } = await baseFetch<RawResponse>({
    endpoint: HOTELS_ENDPOINTS.VALIDATE_COUPON,
    queryParams: { sessionId: params.sessionId, userId: params.userId },
    body: payload,
    logTag: 'HotelValidateCouponClient',
  });

  // Network/HTTP error
  if (error) {
    return {
      type: 'hotels-validate-coupon',
      success: false,
      priceSummary: null,
      statusMessage: null,
      ratePlanList: [],
      appliedCouponCode: null,
      error,
    };
  }

  const priceSummary = transformPriceSummary(data?.priceSummary);
  const statusMessage = data?.statusMessage ?? null;
  const ratePlanList = data?.ratePlanList ?? [];

  // API returned error
  if (data?.success === false && data?.error) {
    logger.warn('[HotelValidateCouponClient] API returned error:', data.error);
    return {
      type: 'hotels-validate-coupon',
      success: false,
      priceSummary,
      statusMessage,
      ratePlanList,
      appliedCouponCode: data.appliedCouponCode ?? null,
      error: data.error,
    };
  }

  // Success
  logger.warn('[HotelValidateCouponClient] Success:', {
    statusMessage,
    hasPriceSummary: priceSummary !== null,
    appliedCouponCode: data?.appliedCouponCode ?? null,
  });
  return {
    type: 'hotels-validate-coupon',
    success: true,
    priceSummary,
    statusMessage,
    ratePlanList,
    appliedCouponCode: data?.appliedCouponCode ?? null,
  };
}
